import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BookkeepingEditingState,
  BookkeepingDocumentItem,
} from "../../models/BookkeepingEditing.model";
import {
  EditableHeaderData,
  DocumentEditingCalculations,
  DocumentDraftContact,
} from "../../models/DocumentEditing.model";
import { ProjectItemDTO } from "../../models/BaseItem.model";
import { convertBookkeepingDocumentTemplate } from "../thunks/bookkeepingEditingThunks";
import { GetTagsValues } from "../thunks/TagsValueThunks";
import { createDocumentTemplateEngine } from "../../utils/documentTemplateEngine";

const documentTemplateEngine = createDocumentTemplateEngine();

const emptyContact: DocumentDraftContact = {
  contactID: 0,
  contactName: "",
  contactIdent: "",
  contactPhone: "",
  contactAddress: "",
  contactEMail: "",
  isManual: false,
};

const emptyHeader: EditableHeaderData = {
  docDate: new Date().toISOString().split("T")[0],
  discountType: 0,
  discountValue: 0,
  vatPercent: 18,
  isRoundedValue: false,
  isVATZero: false,
  remarks: "",
};

const emptyCalculations: DocumentEditingCalculations = {
  itemsSumValue: 0,
  subTotalBeforeDiscount: 0,
  discountAmount: 0,
  subTotal: 0,
  vatValue: 0,
  totalValue: 0,
};

const initialState: BookkeepingEditingState = {
  templateHtml: null,
  templateContext: null,

  draftItems: [],
  draftPayments: [],
  draftHeader: { ...emptyHeader },
  draftContact: { ...emptyContact },

  committedItems: [],
  committedPayments: [],
  committedHeader: { ...emptyHeader },
  committedContact: { ...emptyContact },

  calculations: { ...emptyCalculations },

  docType: 0,
  docDate: "",
  dueDate: "",
  lang: "he",
  contactID: 0,
  allowItems: true,
  allowPayments: true,

  templateLoading: false,
  tagsLoading: false,
  initLoading: false,
  renderedHtml: null,
  lastRenderId: null,
  activeTemplateRequestId: null,
  activeTagsRequestId: null,
};

// ============================================================
// Helpers
// ============================================================

const calculateDocumentItemLineTotal = (
  item: BookkeepingDocumentItem,
  vatPercent: number,
): number => {
  const qty = Number(item.documentItemQty || 0);
  const price = Number(item.documentItemUnitPrice || 0);
  const rate = Number(item.documentItemCurrencyRate || 1);
  const vatType = Number(item.documentItemVATType || 0);

  let total = qty * price * rate;
  if (vatType === 0 && vatPercent > 0) {
    total = total / (1 + vatPercent / 100);
  }
  return total;
};

const normalizePayment = (payment: any) => ({
  ...payment,
  date:
    typeof payment?.date === "string"
      ? payment.date
      : payment?.date instanceof Date
        ? payment.date.toISOString().split("T")[0]
        : "",
});

const mapProjectItemToDocumentItem = (
  p: ProjectItemDTO,
): BookkeepingDocumentItem => ({
  documentItemID: p.projectItemID,
  baseItemID: p.baseItemID || 0,
  documentItemDesc: p.projectItemDesc || "",
  documentItemQty: p.projectItemQty || 0,
  documentItemUnit: p.projectItemUnit || "",
  documentItemUnitPrice: p.projectItemUnitPrice || 0,
  documentItemCurrency: p.projectItemCurrency || "ILS",
  documentItemCurrencyRate: p.projectItemCurrencyRate || 1,
  documentItemVATType: p.projectItemVATType || 0,
  documentItemVATPercent: p.projectItemVATPercent || 18,
  documentItemTotalLine: p.projectItemTotalLine || 0,
  documentItemVATDesc:
    Number(p.projectItemVATType || 0) === 0 ? 'כולל מע"מ' : 'לפני מע"מ',
});

// ============================================================
// Slice
// ============================================================

const bookkeepingEditingSlice = createSlice({
  name: "bookkeepingEditing",
  initialState,
  reducers: {
    // ═══════════════ INIT DRAFT FROM COMMITTED ═══════════════

    initDraftFromCommitted(state) {
      state.draftItems = state.committedItems.map((i) => ({ ...i }));
      state.draftPayments = state.committedPayments.map((p) =>
        normalizePayment(p),
      );
      state.draftHeader = { ...state.committedHeader };
      state.draftContact = { ...state.committedContact };
    },

    // ═══════════════ INITIALIZE ═══════════════

    initializeBookkeepingEditingState(
      state,
      action: PayloadAction<{
        projectItems: ProjectItemDTO[];
        projectItemsHeader: {
          projectItemsHdr_DiscountType: number;
          projectItemsHdr_DiscountValue: number;
          projectItemsHdr_VATPercent: number;
          projectItemsHdr_IsRoundedValue: boolean;
          projectItemsHdr_IsVATZero: boolean;
          projectItemsHdr_Remarks: string;
        } | null;
        contact?: DocumentDraftContact;
        docType: number;
        docDate: string;
        dueDate?: string;
        lang?: string;
        contactID?: number;
        allowItems?: boolean;
        allowPayments?: boolean;
      }>,
    ) {
      const {
        projectItems,
        projectItemsHeader,
        contact,
        docType,
        docDate,
        dueDate,
        lang,
        contactID,
        allowItems,
        allowPayments,
      } = action.payload;

      // Keep items/payments empty initially; loaded from server context on openPanel
      state.committedItems = [];
      state.committedPayments = [];

      if (projectItemsHeader) {
        state.committedHeader = {
          docDate: docDate || new Date().toISOString().split("T")[0],
          discountType: projectItemsHeader.projectItemsHdr_DiscountType,
          discountValue: projectItemsHeader.projectItemsHdr_DiscountValue,
          vatPercent: projectItemsHeader.projectItemsHdr_VATPercent,
          isRoundedValue: projectItemsHeader.projectItemsHdr_IsRoundedValue,
          isVATZero: projectItemsHeader.projectItemsHdr_IsVATZero,
          remarks: projectItemsHeader.projectItemsHdr_Remarks || "",
        };
      } else {
        state.committedHeader = {
          ...emptyHeader,
          docDate: docDate || emptyHeader.docDate,
        };
      }

      if (contact) {
        state.committedContact = { ...contact };
      }

      state.docType = docType;
      state.docDate = docDate;
      if (dueDate !== undefined) state.dueDate = dueDate;
      if (lang !== undefined) state.lang = lang;
      if (contactID !== undefined) state.contactID = contactID;
      if (allowItems !== undefined) state.allowItems = allowItems;
      if (allowPayments !== undefined) state.allowPayments = allowPayments;
      state.templateContext = null;
      state.templateHtml = null;
      state.renderedHtml = null;
      state.lastRenderId = null;
      state.activeTemplateRequestId = null;
      state.activeTagsRequestId = null;

      // Mirror to draft
      state.draftItems = [];
      state.draftPayments = [];
      state.draftHeader = { ...state.committedHeader };
      state.draftContact = { ...state.committedContact };
    },

    // ═══════════════ LOAD FROM SERVER CONTEXT ═══════════════

    /** Load BKDocumentJSONItems from server context into committedItems (first time) */
    loadItemsFromServerContext(
      state,
      action: PayloadAction<BookkeepingDocumentItem[]>,
    ) {
      // Assign unique documentItemID using baseItemID when server sends 0
      const items = action.payload.map((i) => {
        if (i.documentItemID === 0 && i.baseItemID > 0) {
          return { ...i, documentItemID: i.baseItemID };
        }
        return { ...i };
      });
      state.committedItems = items;
      state.draftItems = items.map((i) => ({ ...i }));
    },

    // ═══════════════ DRAFT MUTATIONS ═══════════════

    updateDraftItem(
      state,
      action: PayloadAction<{ itemId: number; field: string; value: any }>,
    ) {
      const { itemId, field, value } = action.payload;
      const item = state.draftItems.find((i) => i.documentItemID === itemId);
      if (item) {
        (item as any)[field] = value;
        if (
          field === "documentItemQty" ||
          field === "documentItemUnitPrice" ||
          field === "documentItemVATType" ||
          field === "documentItemCurrencyRate"
        ) {
          const vatPercent = state.draftHeader.isVATZero
            ? 0
            : state.draftHeader.vatPercent;
          item.documentItemTotalLine = calculateDocumentItemLineTotal(
            item,
            vatPercent,
          );
          // Update VATDesc based on VATType
          item.documentItemVATDesc =
            Number(item.documentItemVATType) === 0 ? 'כולל מע"מ' : 'לפני מע"מ';
        }
      }
    },

    addDraftItem(state) {
      const newId =
        state.draftItems.length > 0
          ? Math.min(...state.draftItems.map((i) => i.documentItemID)) - 1
          : -1;
      state.draftItems.push({
        documentItemID: newId,
        baseItemID: 0,
        documentItemDesc: "",
        documentItemQty: 1,
        documentItemUnit: "",
        documentItemUnitPrice: 0,
        documentItemCurrency: "ILS",
        documentItemCurrencyRate: 1,
        documentItemVATType: 0,
        documentItemVATPercent: state.draftHeader.vatPercent,
        documentItemTotalLine: 0,
        documentItemVATDesc: 'כולל מע"מ',
      });
    },

    removeDraftItem(state, action: PayloadAction<number>) {
      state.draftItems = state.draftItems.filter(
        (i) => i.documentItemID !== action.payload,
      );
    },

    setDraftPayments(state, action: PayloadAction<any[]>) {
      state.draftPayments = action.payload.map((p) => normalizePayment(p));
    },

    importItemsFromProject(state, action: PayloadAction<ProjectItemDTO[]>) {
      const mapped = action.payload.map(mapProjectItemToDocumentItem);
      state.draftItems = mapped;
    },

    updateDraftHeader(
      state,
      action: PayloadAction<Partial<EditableHeaderData>>,
    ) {
      Object.assign(state.draftHeader, action.payload);
    },

    updateDraftContact(
      state,
      action: PayloadAction<Partial<DocumentDraftContact>>,
    ) {
      Object.assign(state.draftContact, action.payload);
    },

    setDraftContactFromList(
      state,
      action: PayloadAction<{
        contactID: number;
        contactName: string;
        contactIdent: string;
        contactPhone: string;
        contactAddress: string;
        contactEMail: string;
      }>,
    ) {
      state.draftContact = {
        ...action.payload,
        isManual: false,
      };
    },

    setDraftContactManual(state) {
      state.draftContact = {
        ...emptyContact,
        contactID: -1,
        isManual: true,
      };
    },

    // ═══════════════ COMMIT ═══════════════

    commitItems(state) {
      state.committedItems = state.draftItems.map((i) => ({ ...i }));
    },

    commitPayments(state) {
      state.committedPayments = state.draftPayments.map((p) =>
        normalizePayment(p),
      );
    },

    commitHeader(state) {
      state.committedHeader = { ...state.draftHeader };
      // Propagate docDate to top-level state
      if (state.draftHeader.docDate) {
        state.docDate = state.draftHeader.docDate;
      }
    },

    commitContact(state) {
      state.committedContact = { ...state.draftContact };
    },

    // ═══════════════ CALCULATIONS ═══════════════

    setCalculations(state, action: PayloadAction<DocumentEditingCalculations>) {
      state.calculations = action.payload;
    },

    // ═══════════════ RENDERED OUTPUT ═══════════════

    setRenderedHtml(
      state,
      action: PayloadAction<{ html: string; renderId: string }>,
    ) {
      state.renderedHtml = action.payload.html;
      state.lastRenderId = action.payload.renderId;
    },

    clearEditingState() {
      return { ...initialState };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(convertBookkeepingDocumentTemplate.pending, (state, action) => {
        state.templateLoading = true;
        state.templateContext = null;
        state.templateHtml = null;
        state.renderedHtml = null;
        state.activeTemplateRequestId = action.meta.requestId;
      })
      .addCase(
        convertBookkeepingDocumentTemplate.fulfilled,
        (state, action) => {
          if (state.activeTemplateRequestId !== action.meta.requestId) {
            return;
          }
          state.templateLoading = false;
          state.templateHtml = action.payload.baseTemplateHtml;
          // TagsValues no longer come from this endpoint —
          // they are fetched separately via GetTagsValues.
        },
      )
      .addCase(convertBookkeepingDocumentTemplate.rejected, (state, action) => {
        if (state.activeTemplateRequestId !== action.meta.requestId) {
          return;
        }
        state.templateLoading = false;
      })
      .addCase(GetTagsValues.pending, (state, action) => {
        state.tagsLoading = true;
        state.activeTagsRequestId = action.meta.requestId;
      })
      // TagsValues fetched separately — compile into context when they arrive
      .addCase(GetTagsValues.fulfilled, (state, action) => {
        if (state.activeTagsRequestId !== action.meta.requestId) {
          return;
        }
        state.tagsLoading = false;
        if (action.payload.tagsValues && action.payload.tagsValues.length > 0) {
          const context = documentTemplateEngine.compileTagsToContext(
            action.payload.tagsValues,
          );
          // Merge with existing context if any (keep server context, override with tags)
          state.templateContext = {
            ...(state.templateContext || {}),
            ...context,
          };
        }
      })
      .addCase(GetTagsValues.rejected, (state, action) => {
        if (state.activeTagsRequestId !== action.meta.requestId) {
          return;
        }
        state.tagsLoading = false;
      });
  },
});

export const {
  initDraftFromCommitted,
  initializeBookkeepingEditingState,
  loadItemsFromServerContext,
  updateDraftItem,
  addDraftItem,
  removeDraftItem,
  setDraftPayments,
  importItemsFromProject,
  updateDraftHeader,
  updateDraftContact,
  setDraftContactFromList,
  setDraftContactManual,
  commitItems,
  commitPayments,
  commitHeader,
  commitContact,
  setCalculations,
  setRenderedHtml,
  clearEditingState,
} = bookkeepingEditingSlice.actions;

export default bookkeepingEditingSlice.reducer;
